import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useContent } from "@/lib/content";
import { formatPrice } from "@/lib/format";

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const { t } = useContent();
  const hasVariants = (product.variants || []).length > 0;
  const stock = Number(product.stock) || 0;
  const soldOut = stock <= 0;
  const low = stock > 0 && stock <= 3;

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col bg-white rounded-3xl border border-line overflow-hidden shadow-[0_10px_40px_rgba(16,114,105,0.06)] hover:shadow-[0_20px_60px_rgba(16,114,105,0.12)] hover:-translate-y-1 transition-all duration-500"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link to={`/produit/${product.slug}`} className="relative block overflow-hidden arch mx-3 mt-3">
        <img
          src={product.images?.[0]}
          alt={product.name}
          loading="lazy"
          className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span
          data-testid={`stock-badge-${product.id}`}
          className={`absolute top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-wider backdrop-blur ${
            soldOut
              ? "bg-[#b04a4a]/90 text-white"
              : low
              ? "bg-tan/95 text-[#7c4a1e]"
              : "bg-teal/90 text-cream"
          }`}
        >
          {soldOut ? t("product.badge_soldout") : low ? t("product.badge_low", { n: String(stock) }) : t("product.badge_available")}
        </span>
      </Link>
      <div className="flex flex-col flex-1 px-5 pb-5 pt-4">
        <Link to={`/produit/${product.slug}`} className="font-serif text-xl text-teal-dark leading-snug">
          {product.name}
        </Link>
        <p className="mt-1 text-xs font-mono uppercase tracking-widest text-mauve">
          {product.category ? product.category.replace(/-/g, " ") : ""}
        </p>
        <div className="mt-auto flex items-center justify-between pt-4">
          <p data-testid={`product-price-${product.id}`} className="font-semibold text-teal">
            {product.old_price ? (
              <span className="mr-2 text-sm text-mauve line-through">{formatPrice(product.old_price)}</span>
            ) : null}
            {formatPrice(product.price)}
          </p>
          <button
            data-testid={`add-to-cart-button-${product.id}`}
            onClick={() => (hasVariants ? navigate(`/produit/${product.slug}`) : add(product))}
            disabled={soldOut}
            aria-label={`Ajouter ${product.name} au panier`}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-mint text-teal-dark transition-all hover:bg-teal hover:text-cream active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}
